import {Component, OnInit} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {HttpTuftProductsService} from '@infrastructure/http/tuft-product/http-tuft-products.service';
import {GetAllTuftProductsParameters} from '@infrastructure/http/tuft-product/tuft-products.interface';
import {AgGridUtils, BaseComponent, ColDefBuilderFactoryService, FractionUtils, LinkIconRendererComponent, StringUtils, Unit} from '@vdw/angular-component-library';
import {ColDef, GridApi, ICellRendererParams, ITooltipParams, ValueGetterParams} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {takeUntil} from 'rxjs';
import {TextileDataType} from '../../textile-data-type.enum';
import {OverviewListTuftProduct} from './overview-list-tuft-product';

@Component({
  templateUrl: './tuft-product-overview-page.component.html'
})
export class TuftProductOverviewPageComponent extends BaseComponent implements OnInit {
  public listOfTuftProducts: OverviewListTuftProduct[];
  public readonly textileDataType = TextileDataType;
  public readonly GRID_IDENTIFIER = GridIdentifier.TUFT_PRODUCT_OVERVIEW;

  private readonly pageUrls = LastModifiedCardUtils.getPageUrls('tuftProduct');

  public constructor(
    public readonly tuftProductsService: HttpTuftProductsService,
    private readonly l10nIntlService: L10nIntlService,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.getTuftProducts();
  }

  public getColumnDefsForListOfTuftProducts(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withComparator(StringUtils.stringComparator)
        .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
          return {
            params,
            editPageUrl: this.pageUrls.edit
          };
        })
        .build(),
      this.colDefBuilderFactoryService.getBuilder().withColIdAndField('description', true).withHeaderName('GENERAL.DESCRIPTION').build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('version', true)
        .withHeaderName('GENERAL.VERSION')
        .withCellRenderer((params: ICellRendererParams) => params.getValue())
        .withCellClass('right')
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('widthInCm')
        .withHeaderName('GENERAL.DIMENSIONS.WIDTH')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.CENTIMETER))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.CENTIMETER, this.l10nIntlService))
        .withCellClass('right')
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('blend', true)
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.BLEND')
        .withCellRenderer((params: ICellRendererParams) => params.getValue())
        .withCellClass('left')
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('color', true)
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.COLOR')
        .withCellRenderer((params: ICellRendererParams) => params.getValue())
        .withCellClass('left')
        .withComparator(StringUtils.stringComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('gauge')
        .withHeaderName('GENERAL.DIMENSIONS.GAUGE')
        .withValueGetter((params: ValueGetterParams) => params.data.gauge.fractionalValueAsString)
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.INCH))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.INCH, this.l10nIntlService))
        .withCellClass('right')
        .withComparator(FractionUtils.fractionComparator)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('stitchRateInStitchesPerCm')
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.STITCH_RATE')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.PER_CENTIMETER))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PER_CENTIMETER, this.l10nIntlService))
        .withCellClass('right')
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('pileHeightInCm')
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.PILE_HEIGHT')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.CENTIMETER))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.CENTIMETER, this.l10nIntlService))
        .withCellClass('right')
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('description', true)
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.DESCRIPTION')
        .withCellRenderer((params: ICellRendererParams) => params.getValue())
        .withCellClass('right')
        .withComparator(StringUtils.stringComparator)
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('weightInGramsPerMeter')
        .withHeaderName('GENERAL.WEIGHT')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.GRAMS_PER_METER))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.GRAMS_PER_METER, this.l10nIntlService))
        .withCellClass('right')
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('primaryBacking')
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.PRIMARY_BACKING')
        .withTooltipValueGetter((params: ITooltipParams) => params.value.name)
        .withValueGetter((params: ValueGetterParams) => `${params.data.primaryBacking.name}`, true)
        .withCellClass('right')
        .withComparator(StringUtils.stringComparator)
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('standardSpeed')
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.STANDARD_SPEED')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.ROTATIONS_PER_MINUTE))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.ROTATIONS_PER_MINUTE, this.l10nIntlService))
        .withCellClass('right')
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('standardEfficiency')
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.STANDARD_EFFICIENCY')
        .withCellRenderer((params: ICellRendererParams) => this.buildAgGridCellTextWithUnit(params.getValue(), Unit.PERCENTAGE))
        .withTooltipValueGetter((params: ITooltipParams) => AgGridUtils.buildAgGridCellTooltipWithUnit(params.value, Unit.PERCENTAGE, this.l10nIntlService))
        .withCellClass('right')
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('tuftingNotes', true)
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.TUFTING_NOTES')
        .withCellRenderer((params: ICellRendererParams) => params.getValue())
        .withCellClass('right')
        .withComparator(StringUtils.stringComparator)
        .withHide(true)
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('backingNotes', true)
        .withHeaderName('TEXTILE_DATA.TUFT_PRODUCT.BACKING_NOTES')
        .withCellRenderer((params: ICellRendererParams) => params.getValue())
        .withCellClass('right')
        .withComparator(StringUtils.stringComparator)
        .withHide(true)
        .build()
    ];
  }

  public exportGridForTuftProducts(gridApi: GridApi): void {
    gridApi.exportDataAsCsv({
      processCellCallback: (params: any) => {
        if (params.column.colId === 'gauge') {
          return `${params.value.fractionalValueAsString} inch`;
        } else if (params.column.colId === 'stitchRateInStitchesPerCm') {
          return `${params.value} /cm`;
        } else if (params.column.colId === 'widthInCm') {
          return `${params.value} cm`;
        } else {
          return params.value;
        }
      }
    });
  }

  public showOnlyLatestVersionChanged(showOnlyLatestVersion: boolean): void {
    this.getTuftProducts(showOnlyLatestVersion);
  }

  private getTuftProducts(showOnlyLatestVersion: boolean = true): void {
    const parameters: Partial<GetAllTuftProductsParameters> = {showOnlyLatestVersion};

    this.tuftProductsService
      .getAll(parameters)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((tuftProducts: OverviewListTuftProduct[]) => (this.listOfTuftProducts = tuftProducts));
  }

  private buildAgGridCellTextWithUnit(value: number | string, defaultUnit: Unit): string {
    return AgGridUtils.buildAgGridCellTextWithUnit(value, defaultUnit, this.l10nIntlService);
  }
}
